import React from "react";
import { Sparkles, Lock, ArrowUpRight } from "lucide-react";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import Card from "../UI/Card";
import Button from "../UI/Button";

const TaskCard = ({
    task,
    isUpcoming,
    isPremium,
    selectedProducts,
    onTaskClick,
}) => {
    const { content } = useContentfulContext();

    const isDisabled =
        !task.is_accessible_to_org || !task.is_accessible_to_user;
    const filteredProducts = (task.products || []).filter((product) =>
        selectedProducts.includes(product.id),
    );

    const handleTaskClick = (e) => {
        if (e) e.preventDefault();
        if (isDisabled) return;
        onTaskClick(task.id);
    };

    // Generate hover message for inaccessible tasks
    const getHoverMessage = () => {
        if (!task.is_accessible_to_org && !task.is_accessible_to_user) {
            return content?.TASKS_CONTENT?.hover_messages?.message_1;
        }
        if (!task.is_accessible_to_org) {
            return content?.TASKS_CONTENT?.hover_messages?.message_2;
        }
        if (!task.is_accessible_to_user) {
            return content?.TASKS_CONTENT?.hover_messages?.message_3;
        }
        return null;
    };

    return (
        <Card
            id={`function-card-${task.id}`}
            variant={isDisabled ? "default" : "accent"}
            padding="none"
            onClick={handleTaskClick}
            className={`group relative flex flex-col justify-between p-5 rounded-xl border transition-all duration-200 ${
                isDisabled
                    ? "bg-slate-50/80 border-border opacity-70 cursor-not-allowed"
                    : "bg-gradient-to-b from-white via-white to-primary-soft/20 border-border-subtle hover:border-primary/40 hover:shadow-card-hover hover:-translate-y-0.5 cursor-pointer"
            }`}
        >
            <div>
                {/* Top row: Icon & Products / Badges */}
                <div className="flex items-start justify-between gap-3">
                    <h5
                        className="text-base font-semibold text-stride-heading mb-4 line-clamp-1 group-hover:text-primary transition-colors"
                        title={task.title}
                    >
                        {task.title}
                    </h5>

                    <div className="flex items-center gap-1.5 shrink-0">
                        <ProductDisplay
                            item={task}
                            selectedProducts={selectedProducts}
                            filteredProducts={filteredProducts}
                        />
                        {isPremium && (
                            <div className="relative">
                                <button
                                    type="button"
                                    aria-label="Premium feature"
                                    className="tooltip-button w-6 h-6 rounded-md flex items-center justify-center text-amber-600 bg-amber-50 border border-amber-200/80 hover:bg-amber-100 transition-colors"
                                >
                                    <Lock size={12} />
                                </button>
                                <div
                                    role="tooltip"
                                    className="tooltip-content absolute right-0 top-full mt-1.5 z-20 invisible inline-block px-2.5 py-1.5 text-xs font-medium text-white bg-slate-900 rounded-lg shadow-md opacity-0 transition-opacity whitespace-nowrap"
                                >
                                    {content?.TASKS_CONTENT?.premium_tooltip}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Title & Description */}

                <p
                    className="text-xs text-stride-muted leading-relaxed line-clamp-2 min-h-[32px]"
                    title={task.description}
                >
                    {task.description}
                </p>
            </div>

            {/* Unified Action Footer */}
            <div className="mt-5 pt-3 border-t border-border-subtle/80 flex items-center justify-between gap-2">
                {isDisabled ? (
                    <div className="w-full relative group/disabled">
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-center opacity-60 cursor-not-allowed"
                            disabled
                        >
                            {content?.TASKS_CONTENT?.disabled_text ||
                                "Unavailable"}
                        </Button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/disabled:block bg-slate-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-md whitespace-nowrap z-30">
                            {getHoverMessage()}
                        </div>
                    </div>
                ) : (
                    <Button
                        variant="secondary"
                        size="sm"
                        icon={<ArrowUpRight size={14} />}
                        className="w-full justify-center"
                    >
                        {isUpcoming && task?.launch_text
                            ? task.launch_text
                            : "Open Function"}
                    </Button>
                )}
            </div>
        </Card>
    );
};

// Helper component to display products
const ProductDisplay = ({ item, selectedProducts, filteredProducts }) => {
    const { content } = useContentfulContext();

    const renderProductNames = () => {
        if (selectedProducts.length === 0 && item.products?.length > 0) {
            return (
                <span>
                    {item.products[0].name}
                    {item.products.length > 1 && (
                        <span className="ml-1 text-[11px] font-semibold text-stride-primary">
                            (+{item.products.length - 1})
                        </span>
                    )}
                </span>
            );
        } else if (filteredProducts.length > 0) {
            return (
                <span>
                    {filteredProducts[0].name}
                    {filteredProducts.length > 1 && (
                        <span className="ml-1 text-[11px] font-semibold text-stride-primary">
                            (+{filteredProducts.length - 1})
                        </span>
                    )}
                </span>
            );
        }
        return (
            <span>
                {content?.TASKS_CONTENT?.no_product_available || "No Product"}
            </span>
        );
    };

    const renderProductList = (products) => (
        <div className="absolute right-0 top-full mt-1.5 hidden group-hover:block bg-white border border-stride-border shadow-dropdown rounded-lg p-2 min-w-36 z-20">
            <div className="flex flex-col space-y-1">
                {products.map((product) => (
                    <p
                        key={product._id || product.id}
                        className="text-stride-heading text-xs font-medium px-2 py-1 rounded hover:bg-stride-bg-subtle transition-colors"
                    >
                        {product.name}
                    </p>
                ))}
            </div>
        </div>
    );

    return (
        <div
            className="relative inline-block group"
            onClick={(e) => e.stopPropagation()}
        >
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-primary-soft text-primary border border-primary/20 cursor-default">
                {renderProductNames()}
            </span>

            {selectedProducts.length === 0 &&
                item.products?.length > 1 &&
                renderProductList(item.products)}
            {filteredProducts.length > 1 && renderProductList(filteredProducts)}
        </div>
    );
};

export default TaskCard;
